/**
 * Utility functions for working with localStorage
 * Provides type-safe methods for storing, retrieving, and removing data
 */

/**
 * Get a value from localStorage
 * @param key - The key to retrieve
 * @param defaultValue - Optional default value if key doesn't exist
 * @returns The parsed value or default value
 */
export const getItem = <T = any>(key: string, defaultValue?: T): T | null => {
    try {
        const item = localStorage.getItem(key)
        if (item === null) {
            return defaultValue !== undefined ? defaultValue : null
        }
        return JSON.parse(item) as T
    } catch (error) {
        console.error(`Error getting item from localStorage: ${key}`, error)
        return defaultValue !== undefined ? defaultValue : null
    }
}

/**
 * Set a value in localStorage
 * @param key - The key to store
 * @param value - The value to store (will be JSON stringified)
 * @returns boolean indicating success
 */
export const setItem = <T = any>(key: string, value: T): boolean => {
    try {
        const serializedValue = JSON.stringify(value)
        localStorage.setItem(key, serializedValue)
        return true
    } catch (error) {
        console.error(`Error setting item in localStorage: ${key}`, error)
        return false
    }
}

/**
 * Remove a value from localStorage
 * @param key - The key to remove
 * @returns boolean indicating success
 */
export const removeItem = (key: string): boolean => {
    try {
        localStorage.removeItem(key)
        return true
    } catch (error) {
        console.error(`Error removing item from localStorage: ${key}`, error)
        return false
    }
}

/**
 * Clear all items from localStorage
 * @returns boolean indicating success
 */
export const clear = (): boolean => {
    try {
        localStorage.clear()
        return true
    } catch (error) {
        console.error('Error clearing localStorage', error)
        return false
    }
}

/**
 * Check if a key exists in localStorage
 * @param key - The key to check
 * @returns boolean indicating if key exists
 */
export const hasItem = (key: string): boolean => {
    try {
        return localStorage.getItem(key) !== null
    } catch (error) {
        console.error(`Error checking item in localStorage: ${key}`, error)
        return false
    }
}

/**
 * Get all keys from localStorage
 * @returns Array of all keys
 */
export const getAllKeys = (): string[] => {
    try {
        return Object.keys(localStorage)
    } catch (error) {
        console.error('Error getting all keys from localStorage', error)
        return []
    }
}

/**
 * Get the size of localStorage (number of items)
 * @returns Number of items in localStorage
 */
export const size = (): number => {
    try {
        return localStorage.length
    } catch (error) {
        console.error('Error getting localStorage size', error)
        return 0
    }
}

// Default export with all methods
export default {
    getItem,
    setItem,
    removeItem,
    clear,
    hasItem,
    getAllKeys,
    size
}
